/**
 * Core handler call: POST to _schd/.../call/{extension}/{handler} or .../call/{extension}/{handler}/{subhandler}.
 * Payload is merged with portfolio/org from config.
 */

import { getAuthHeaders, getPayloadBase, buildCallUrl } from './request';

const DEFAULT_SYNC_TIMEOUT_MS = 15 * 60 * 1000; // 15 minutes

export interface HandlerCallResponse {
  action?: string;
  handler?: string;
  input?: Record<string, unknown>;
  interface?: unknown;
  output?: Record<string, unknown>;
  stack?: Record<string, unknown>;
  success?: boolean;
  [key: string]: unknown;
}

async function postHandler(
  url: string,
  body: Record<string, unknown>,
  timeoutMs: number
): Promise<HandlerCallResponse> {
  const headers = await getAuthHeaders();
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    const data = (await res.json().catch(() => ({}))) as HandlerCallResponse & {
      error?: string;
      message?: string;
    };
    clearTimeout(timeoutId);
    if (!res.ok) {
      throw new Error(
        (data?.error as string) || (data?.message as string) || `HTTP ${res.status}`
      );
    }
    return data;
  } catch (e) {
    clearTimeout(timeoutId);
    if (e instanceof Error) {
      if (e.name === 'AbortError') {
        throw new Error(`Handler call timed out after ${timeoutMs / 60000} minutes`);
      }
      throw e;
    }
    throw new Error(String(e));
  }
}

/**
 * Calls a sync handler. Payload is merged with portfolio/org from config.
 * URL: .../call/{extension}/{handler}
 *
 * @param extension e.g. 'arbitium'
 * @param handler e.g. 'scan_candidates'
 * @param payload optional partial payload; portfolio and org are added in core
 * @param timeoutMs default 15 minutes
 */
export async function callHandler(
  extension: string,
  handler: string,
  payload: Record<string, unknown> = {},
  timeoutMs: number = DEFAULT_SYNC_TIMEOUT_MS
): Promise<HandlerCallResponse> {
  const base = getPayloadBase();
  const body = { ...base, ...payload };
  const url = buildCallUrl(extension, handler);
  return postHandler(url, body, timeoutMs);
}

/**
 * Calls a sync handler with a subhandler in the path. Payload is merged with portfolio/org from config.
 * URL: .../call/{extension}/{handler}/{subhandler}
 *
 * @param extension e.g. 'arbitium'
 * @param handler e.g. 'aws_threats'
 * @param subhandler e.g. 'public_s3_bucket'
 * @param payload optional partial payload; portfolio and org are added in core
 * @param timeoutMs default 15 minutes
 */
export async function callHandlerSubhandler(
  extension: string,
  handler: string,
  subhandler: string,
  payload: Record<string, unknown> = {},
  timeoutMs: number = DEFAULT_SYNC_TIMEOUT_MS
): Promise<HandlerCallResponse> {
  const base = getPayloadBase();
  const body = { ...base, ...payload };
  const url = buildCallUrl(extension, handler, subhandler);
  return postHandler(url, body, timeoutMs);
}
